import React from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import SettingsIcon from "@material-ui/icons/Settings";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import Tooltip from "@material-ui/core/Tooltip";
import { green } from "@material-ui/core/colors";
import PropTypes from "prop-types";
import {
    formatMoney,
    formatDate,
    formatGuestName,
    formatStatus,
    renderSortArrow,
} from "../../lib/helperFuncs.jsx";
import {
    UNAVAILABILITY_MESSAGE,
    FIRST_PAGE,
    PREV_PAGE,
    NEXT_PAGE,
    LAST_PAGE,
    ASC,
    DESC,
    sortableFields,
    MATERIAL_UI_PROPS,
} from "../../lib/data";
import css from "../../../../sass/reservations_grid.module.scss";

const useStyles = makeStyles({
    root: {
        width: "100%",
    },
});

const SubscriptionsGrid = (props) => {
    const createData = (subscriptions, control, fields) => {
        const { id, name, email } = { ...subscriptions };
        const created_at = formatDate(subscriptions.created_at);

        const roomTypeIds = subscriptions.room_type_id
            .split(",")
            .map((id) => id.trim());
        const roomNames = roomTypeIds.map((id) => props?.roomTypesShort?.[id]);
        let roomName = roomNames.join(", ");
        if (roomName.length === 0) roomName = "---";

        const date_in = formatDate(subscriptions.date_in);
        const date_out = formatDate(subscriptions.date_out);
        const status = formatStatus(subscriptions.status, fields);
        const promo_code = subscriptions?.promo_name ?? "---";
        const name_guest = name;
        return {
            id,
            name_guest,
            email,
            promo_code,
            created_at,
            roomName,
            date_in,
            date_out,
            status,
        };
    };

    const classes = useStyles();
    const rows = props.resData.AllItems.map((res) =>
        createData(res, undefined, props.fields),
    );

    const sortReservations = (params) => {
        const sortParams = { ...params };
        const { sortField } = { ...sortParams };
        if (!sortableFields.includes(sortField)) return undefined;

        switch (sortParams.sortOrder) {
            case ASC:
                sortParams.sortOrder = DESC;
                break;
            case DESC:
                sortParams.sortOrder = ASC;
                break;
        }

        props.updateGlobalSearch(
            {
                ...props.globalSearch.search[props.versionFilter],
                sortOrder: sortParams.sortOrder,
                sortField,
            },
            props.versionFilter,
        );

        return props.paramsChangeHandler({
            ...props.getParams,
            sortField: sortField,
            sortOrder: sortParams.sortOrder,
        });
    };

    const updatingEnvironment = (page) => {
        props.paramsChangeHandler({
            ...props.getParams,
            page,
        });
        props.updateGlobalSearch(
            {
                ...props.globalSearch.search[props.versionFilter],
                page,
            },
            props.versionFilter,
        );
    };

    const changePage = (page) => {
        const params = {
            sortOrder: props.sortOrder,
            sortField: props.sortField,
        };

        switch (page) {
            case FIRST_PAGE:
                if (props.resData.currentPage !== 1) {
                    updatingEnvironment(1);
                }
                break;
            case PREV_PAGE:
                if (
                    props.resData.prevPageUrl &&
                    1 !== props.resData.currentPage
                ) {
                    const prevPageUrlArr = props.resData.prevPageUrl.split("=");
                    const prevPageNum =
                        prevPageUrlArr[prevPageUrlArr.length - 1];

                    updatingEnvironment(prevPageNum);
                }
                break;
            case NEXT_PAGE:
                if (
                    props.resData.nextPageUrl &&
                    props.resData.lastPage !== props.resData.currentPage
                ) {
                    const nextPageUrlArr = props.resData.nextPageUrl.split("=");
                    const nextPageNum =
                        nextPageUrlArr[nextPageUrlArr.length - 1];

                    updatingEnvironment(nextPageNum);
                }
                break;
            case LAST_PAGE:
                if (props.resData.lastPage !== props.resData.currentPage) {
                    updatingEnvironment(props.resData.lastPage);
                }
        }
    };

    return (
        <Paper className={classes.root}>
            <Table aria-label="custom pagination table">
                <TableHead>
                    <TableRow key="header">
                        {props.columns.map((column) => (
                            <TableCell
                                key={column.id}
                                align={column.align}
                                style={{
                                    minWidth: column.minWidth,
                                    fontWeight: 400,
                                }}
                                onClick={() => {
                                    sortReservations({
                                        sortField: column.id,
                                        sortOrder: props.getParams.sortOrder,
                                        page: props.resData.currentPage,
                                    });
                                }}
                            >
                                {column.label}
                                {renderSortArrow(
                                    props.getParams.sortField,
                                    props.getParams.sortOrder,
                                    column.id,
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        let colorStatus = "";
                        if (row.status === "Отправлено письмо")
                            colorStatus = "#bee9c0";

                        if (row.status === "Требуется поиск")
                            colorStatus = "#eee48e";

                        return (
                            <TableRow
                                hover
                                tabIndex={-1}
                                key={`${row.id}/${row.created_at}`}
                            >
                                {props.columns.map((column) => {
                                    let value = row[column.id];
                                    return (
                                        <TableCell
                                            key={`${value}/${column.id}`}
                                            align={column.align}
                                            style={{
                                                backgroundColor: colorStatus,
                                            }}
                                        >
                                            {value}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                    <TableRow>
                        <TableCell colSpan={10} align={"right"}>
                            <span
                                style={{
                                    verticalAlign: "middle",
                                    margin: "1rem",
                                }}
                            >
                                На странице:{" "}
                                {`${props.resData.AllItems.length}/${props.resData.perPage}`}
                            </span>
                            <span style={{ verticalAlign: "middle" }}>
                                <Tooltip title={"Первая страница"}>
                                    <span
                                        onClick={() => changePage(FIRST_PAGE)}
                                        style={{ margin: "1rem" }}
                                        className={
                                            1 !== props.resData.currentPage
                                                ? css["pagination-arrows"]
                                                : css[
                                                      "pagination-arrows__inactive"
                                                  ]
                                        }
                                    >
                                        <ArrowBackIosIcon
                                            fontSize={
                                                MATERIAL_UI_PROPS.sizes.small
                                            }
                                        />
                                        <ArrowBackIosIcon
                                            fontSize={
                                                MATERIAL_UI_PROPS.sizes.small
                                            }
                                        />
                                    </span>
                                </Tooltip>
                                <Tooltip title={"Предыдущая страница"}>
                                    <span
                                        style={{ margin: "0.5rem" }}
                                        onClick={() => changePage(PREV_PAGE)}
                                        className={
                                            1 !== props.resData.currentPage
                                                ? css["pagination-arrows"]
                                                : css[
                                                      "pagination-arrows__inactive"
                                                  ]
                                        }
                                    >
                                        <ArrowBackIosIcon
                                            fontSize={
                                                MATERIAL_UI_PROPS.sizes.small
                                            }
                                        />
                                    </span>
                                </Tooltip>
                                <div style={{ display: "inline-block" }}>
                                    <span
                                        style={{
                                            fontWeight: "900",
                                            fontSize: "21px",
                                            position: "relative",
                                            top: "-2px",
                                        }}
                                    >
                                        {props.globalSearch.search[
                                            props.versionFilter
                                        ].page ?? 1}
                                    </span>
                                </div>
                                <Tooltip title={"Следующая страница"}>
                                    <span
                                        style={{ margin: "0.5rem" }}
                                        onClick={() => changePage(NEXT_PAGE)}
                                        className={
                                            props.resData.lastPage !==
                                            props.resData.currentPage
                                                ? css["pagination-arrows"]
                                                : css[
                                                      "pagination-arrows__inactive"
                                                  ]
                                        }
                                    >
                                        <ArrowForwardIosIcon
                                            fontSize={
                                                MATERIAL_UI_PROPS.sizes.small
                                            }
                                        />
                                    </span>
                                </Tooltip>
                                <Tooltip title={"Последняя страница"}>
                                    <span
                                        onClick={() => changePage(LAST_PAGE)}
                                        style={{ margin: "1rem" }}
                                        className={
                                            props.resData.lastPage !==
                                            props.resData.currentPage
                                                ? css["pagination-arrows"]
                                                : css[
                                                      "pagination-arrows__inactive"
                                                  ]
                                        }
                                    >
                                        <ArrowForwardIosIcon
                                            fontSize={
                                                MATERIAL_UI_PROPS.sizes.small
                                            }
                                        />
                                        <ArrowForwardIosIcon
                                            fontSize={
                                                MATERIAL_UI_PROPS.sizes.small
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            </span>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Paper>
    );
};

export default SubscriptionsGrid;
